import {
  put,
  takeLatest,
  call,
} from 'redux-saga/effects';
import _ from 'lodash';
import queryString from 'query-string';
import { VERIFY_EMAIL, CONTINUE_AFTER_EMAIL_VERIFICATION } from '../constants';
import {
  verifyEmailSuccess,
  verifyEmailFailure,
} from '../actions';
import { fetchUser } from '../../services/auth';
import { buildLoginPath } from '../../common/lib/redirectToLogin';
import { isClaimedByOtherTab } from '../../common/lib/registrationFlowSession';
import { createNavigateAfterEmailVerified } from './navigateAfterEmailVerifiedSaga';

const EXPIRED_COPY =
  'This verification link has expired. Please request a new verification email.';

const LINK_EXPIRED_ERROR_CODE = 'PutEmailVerifyLinkExpired';

const extractVerifyEmailError = (responseData) => {
  const errorMessage = _.get(responseData, 'errors[0].message', false);
  const errorDetail = _.get(responseData, 'errors[0].detail', false) || errorMessage;
  const linkExpired = errorMessage === LINK_EXPIRED_ERROR_CODE;

  return {
    errorDetail,
    linkExpired,
  };
};

const createVerifyEmailSagas = (api) => {
  const navigateAfterEmailVerified = createNavigateAfterEmailVerified(api);

  function* confirmVerifyEmailSaga() {
    try {
      const queryParams = queryString.parse(window.location.search);
      const clientId = _.get(queryParams, 'clientId', false);
      const secret = _.get(queryParams, 'secret', false);
      const email = _.get(queryParams, 'email', false);
      const code = _.get(queryParams, 'code', false);
      const exp = Number(_.get(queryParams, 'exp', false));

      if (!exp || !Number.isFinite(exp)) {
        yield put(verifyEmailFailure({
          statusCode: 400,
          errorDetail: EXPIRED_COPY,
          linkExpired: true,
        }));
        return;
      }

      const response = yield call(api.verifyEmail, {
        clientId: clientId && decodeURIComponent(clientId),
        secret: secret && decodeURIComponent(secret),
        email: email && decodeURIComponent(email),
        code: code && decodeURIComponent(code),
        exp,
      });
      const statusCode = _.get(response, 'status', false);
      if (statusCode === 200) {
        yield put(verifyEmailSuccess());
      } else {
        const { errorDetail, linkExpired } = extractVerifyEmailError(_.get(response, 'data', {}));
        yield put(verifyEmailFailure({
          statusCode,
          errorDetail,
          linkExpired,
        }));
      }
    } catch (error) {
      const statusCode = _.get(error, 'response.status', false);
      const { errorDetail, linkExpired } = extractVerifyEmailError(
        _.get(error, 'response.data', {}),
      );
      yield put(verifyEmailFailure({
        statusCode,
        errorDetail: errorDetail || (linkExpired ? EXPIRED_COPY : false),
        linkExpired,
      }));
    }
  }

  function* continueAfterEmailVerificationSaga(action) {
    const { push } = action.payload;

    if (isClaimedByOtherTab()) {
      push(buildLoginPath());
      return;
    }

    const { accessToken } = fetchUser();

    if (!accessToken) {
      push(buildLoginPath());
      return;
    }

    yield call(navigateAfterEmailVerified, push);
  }

  function* watchVerifyEmail() {
    yield takeLatest(VERIFY_EMAIL, confirmVerifyEmailSaga);
  }

  function* watchContinueAfterEmailVerification() {
    yield takeLatest(CONTINUE_AFTER_EMAIL_VERIFICATION, continueAfterEmailVerificationSaga);
  }

  return {
    watchVerifyEmail,
    watchContinueAfterEmailVerification, 
  };
};

export default createVerifyEmailSagas;
